import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Send, Loader2, Settings, Sparkles, Wand2, Globe, FileText, Video, Share2 } from 'lucide-react';
import { mockAIProviders, mockResponses } from '../mock';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ChatEnhanced = () => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [selectedModels, setSelectedModels] = useState(
    mockAIProviders.filter(p => p.enabled).map(p => p.id)
  );
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();
  const { user, isPaid } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSend = async () => {
    if (!message.trim()) return;

    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to use AI chat.',
        variant: 'destructive',
      });
      return;
    }

    const userMessage = message;
    setMessage('');
    setLoading(true);

    const newChat = {
      id: Date.now(),
      question: userMessage,
      responses: [],
      synthesized: null
    };

    setChatHistory(prev => [...prev, newChat]);

    try {
      // Call real AI API
      const response = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        text: userMessage,
        user_id: user.id,
        session_id: sessionId,
        is_paid_user: isPaid()
      });

      if (response.data.success) {
        let responses = [];

        if (response.data.tier === 'premium') {
          // Paid users get multiple model responses
          responses = response.data.responses.map((resp, index) => ({
            providerId: `model-${index}`,
            providerName: resp.model,
            response: resp.response,
            timestamp: new Date().toISOString()
          }));
        } else {
          // Free users get single response
          responses = [{
            providerId: 'gpt-4o-mini',
            providerName: 'GPT-4o-mini (Free)',
            response: response.data.response,
            timestamp: new Date().toISOString()
          }];
        }

        setChatHistory(prev => 
          prev.map(chat => 
            chat.id === newChat.id ? { ...chat, responses } : chat
          )
        );

        toast({
          title: 'Response received',
          description: response.data.tier === 'premium' 
            ? `Received responses from ${responses.length} AI models` 
            : 'Received response from GPT-4o-mini',
        });
      }
    } catch (error) {
      console.error('AI chat error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to get AI response. Please try again.',
        variant: 'destructive',
      });

      // Remove the failed chat
      setChatHistory(prev => prev.filter(chat => chat.id !== newChat.id));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSynthesize = (chatId) => {
    setSynthesizing(true);
    
    setTimeout(() => {
      const chat = chatHistory.find(c => c.id === chatId);
      const responsesToMerge = selectedResponses.length > 0 
        ? chat.responses.filter(r => selectedResponses.includes(r.providerId))
        : chat.responses;

      const synthesized = `🎯 **SYNTHESIZED RESPONSE** (Combined insights from ${responsesToMerge.length} AI model${responsesToMerge.length > 1 ? 's' : ''})\n\n` +
        `Based on the analysis from multiple AI models, here's the optimized response:\n\n` +
        `${responsesToMerge.map((r, i) => {
          const provider = getProviderInfo(r.providerId, r.providerName);
          return `**Key Point ${i + 1}** (from ${provider?.name}):\n${r.response.slice(0, 200)}${r.response.length > 200 ? '...' : ''}`;
        }).join('\n\n')}\n\n` +
        `**Conclusion**: This synthesized response combines the best aspects of all model outputs, providing you with a comprehensive and balanced answer.`;

      setChatHistory(prev => 
        prev.map(chat => 
          chat.id === chatId ? { ...chat, synthesized } : chat
        )
      );
      
      setSynthesizing(false);
      setSelectedResponses([]);
      
      toast({
        title: 'Response Synthesized!',
        description: 'Combined insights from multiple AI models.',
      });
    }, 1500);
  };

  const toggleResponseSelection = (chatId, providerId) => {
    setSelectedResponses(prev => {
      if (prev.includes(providerId)) {
        return prev.filter(id => id !== providerId);
      } else {
        return [...prev, providerId];
      }
    });
  };

  const getProviderInfo = (id, providerName) => {
    // Return provider info from providerName if available
    if (providerName) {
      return {
        name: providerName,
        provider: 'AI Model',
        logo: null
      };
    }
    return mockAIProviders.find(p => p.id === id) || { name: id, provider: 'AI Model' };
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Multi-AI Chat with Synthesis</h1>
            <p className="text-sm text-gray-400">Compare and merge responses from multiple AI models</p>
          </div>
          <Link to="/api-settings">
            <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
              <Settings className="w-4 h-4 mr-2" />
              API Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {chatHistory.length === 0 ? (
            <div className="text-center py-20">
              <div className="inline-block p-6 bg-blue-500/10 rounded-full mb-4">
                <Send className="w-12 h-12 text-blue-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">Start a conversation</h2>
              <p className="text-gray-400">Ask a question and get responses from {selectedModels.length} AI models</p>
            </div>
          ) : (
            chatHistory.map((chat) => (
              <div key={chat.id} className="space-y-4">
                {/* User Question */}
                <div className="flex justify-end">
                  <div className="bg-blue-600 text-white px-6 py-3 rounded-2xl rounded-tr-sm max-w-2xl">
                    {chat.question}
                  </div>
                </div>

                {/* AI Responses */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {chat.responses.map((resp) => {
                    const provider = getProviderInfo(resp.providerId, resp.providerName);
                    const isSelected = selectedResponses.includes(resp.providerId);
                    
                    return (
                      <Card 
                        key={resp.providerId} 
                        onClick={() => toggleResponseSelection(chat.id, resp.providerId)}
                        className={`bg-gray-800/50 border-gray-700 p-4 hover:bg-gray-800/70 transition-all duration-300 cursor-pointer ${
                          isSelected ? 'ring-2 ring-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-700">
                          {provider?.logo && (
                            <img src={provider.logo} alt={provider.name} className="w-6 h-6" />
                          )}
                          <div className="flex-1">
                            <p className="font-semibold text-white text-sm">{provider?.name}</p>
                            <p className="text-xs text-gray-400">{provider?.provider}</p>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{resp.response}</p>
                      </Card>
                    );
                  })}
                </div>

                {/* Synthesize Button */}
                {chat.responses.length > 0 && !chat.synthesized && (
                  <div className="flex justify-center">
                    <Button 
                      onClick={() => handleSynthesize(chat.id)}
                      disabled={synthesizing}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {synthesizing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Synthesizing...</>
                      ) : (
                        <><Wand2 className="w-4 h-4 mr-2" /> Synthesize Responses {selectedResponses.length > 0 ? `(${selectedResponses.length})` : '(All)'}</>
                      )}
                    </Button>
                  </div>
                )}

                {/* Synthesized Response */}
                {chat.synthesized && (
                  <div className="space-y-4">
                    <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-500/50 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-6 h-6 text-purple-400" />
                        <h3 className="text-xl font-bold text-white">Optimized Response</h3>
                      </div>
                      <div className="text-gray-200 whitespace-pre-wrap">{chat.synthesized}</div>
                    </Card>

                    {/* Action Buttons */}
                    <Card className="bg-gray-800/50 border-gray-700 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-blue-400" />
                        <h4 className="text-lg font-semibold text-white">Take Action with This Response</h4>
                      </div>
                      <p className="text-gray-400 text-sm mb-4">
                        Turn this AI response into something useful. Choose an action below:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Button
                          onClick={() => {
                            const websiteData = {
                              topic: chat.question,
                              content: chat.synthesized
                            };
                            localStorage.setItem('websiteData', JSON.stringify(websiteData));
                            window.open('/website-generator', '_blank');
                          }}
                          className="bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2"
                        >
                          <Globe className="w-4 h-4" />
                          Generate Website
                        </Button>
                        <Button
                          onClick={() => {
                            const blogData = {
                              topic: chat.question,
                              content: chat.synthesized
                            };
                            localStorage.setItem('blogData', JSON.stringify(blogData));
                            window.open('/automation', '_blank');
                          }}
                          className="bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          Create Blog Post
                        </Button>
                        <Button
                          onClick={() => {
                            const videoData = {
                              topic: chat.question,
                              content: chat.synthesized
                            };
                            localStorage.setItem('videoData', JSON.stringify(videoData));
                            window.open('/automation', '_blank');
                          }}
                          className="bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2"
                        >
                          <Video className="w-4 h-4" />
                          Generate Video Script
                        </Button>
                        <Button
                          onClick={() => {
                            const socialData = {
                              message: chat.question,
                              content: chat.synthesized
                            };
                            localStorage.setItem('socialData', JSON.stringify(socialData));
                            window.open('/automation', '_blank');
                          }}
                          className="bg-purple-600 hover:bg-purple-700 flex items-center justify-center gap-2"
                        >
                          <Share2 className="w-4 h-4" />
                          Social Media Post
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}

                {loading && chat.responses.length === 0 && (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-2 items-end">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask anything to multiple AI models..."
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 resize-none min-h-[60px] max-h-[200px]"
              rows={2}
            />
            <Button 
              onClick={handleSend} 
              disabled={loading || !message.trim()}
              className="bg-blue-600 hover:bg-blue-700 h-[60px] px-6"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Chatting with {selectedModels.length} AI models • Click responses to select for synthesis
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatEnhanced;